import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/utils/api';

/**
 * BP6 (Codex launch audit): self-service password recovery — request step.
 * The server always answers generically; mirror that here (no account hints).
 */
const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSending(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Forgot your password?</h1>
        {sent ? (
          <>
            <p className="text-gray-600 mb-6">
              If an account exists for that email, we've sent a reset link. It's valid for one hour.
            </p>
            <Link to="/login" className="text-blue-600 hover:underline">Back to sign in</Link>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-gray-600">Enter the email for your account and we'll send you a reset link.</p>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                id="email" type="email" required autoComplete="email"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={email} onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit" disabled={sending}
              className="w-full bg-blue-600 text-white rounded-md py-2 font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Send reset link'}
            </button>
            <Link to="/login" className="block text-sm text-blue-600 hover:underline">Back to sign in</Link>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
